<!-- ContactFormStandard.vue -->
<script setup lang="ts">
import { ref, reactive, computed, watch, onBeforeMount } from 'vue'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Spinner from '@/components/Spinner.vue'
import Lottie from '@/components/Lottie.vue'
import Button from '@/components/Button.vue'

//
// — UI state & panels —
//
const formPanel = ref<'form' | 'success'>('form')
const sendDelay = ref(false)

// when we switch to the success panel, delay the final message
watch(formPanel, val => {
  if (val === 'success') {
    setTimeout(() => sendDelay.value = true, 4000)
  } else {
    sendDelay.value = false
  }
})

//
// — Form models —
//
const name    = ref('')
const email   = ref('')
const contact = ref('')
const message = ref('')

//
// — Submission state —
//
const working     = ref(false)
const messageSent = ref(false)

onBeforeMount(() => {
  working.value     = false
  messageSent.value = false
})

//
// — Validation state —
//
const validationMode = ref(false)

const errors = reactive({
  name:    '',
  email:   '',
  contact: '',
  message: ''
})

const EMAIL_REGEX = /^\S+@\S+\.\S+$/

const isFormValid = computed(() => {
  return (
    name.value.trim().length >= 3 &&
    EMAIL_REGEX.test(email.value) &&
    contact.value.trim().length >= 3 &&
    message.value.trim().length >= 10
  )
})

/**
 * Only show the “please fix errors” banner
 * once they’ve tried to submit, and only
 * when there’s at least one error.
 */
const notifications = computed(() => {
  if (!validationMode.value) return false
  return !!(
    errors.name ||
    errors.email ||
    errors.contact ||
    errors.message
  )
})

//
// — Submission / validation logic —
//
async function processSubmit() {
  working.value = true

  // build payload (including page URL)
  const payload = {
    name:    name.value,
    email:   email.value,
    contact: contact.value,
    message: message.value,
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    pageTitle: typeof document !== 'undefined' ? document.title : ''
  }

  try {
    const res = await $fetch('/api/contact', {
      method: 'POST',
      body: payload
    })

    working.value = false
    messageSent.value = true
    formPanel.value   = 'success'

    // if in dev, Ethereal preview URL:
    if (res.preview) {
      // eslint-disable-next-line no-console
      console.log('Preview your email at: ', res.preview)
    }
  } catch (err: any) {
    working.value = false
    validationMode.value = true

    const missing = Array.isArray(err?.data?.missingFields)
      ? (err.data.missingFields as string[])
      : []

    if (missing.includes('name')) {
      errors.name = 'Name must be at least 3 characters.'
    }
    if (missing.includes('email')) {
      errors.email = 'Please enter a valid email address.'
    }
    if (missing.includes('contact')) {
      errors.contact = 'Please enter a valid contact.'
    }
    if (missing.includes('message')) {
      errors.message = 'Message must be at least 10 characters.'
    }

    if (!missing.length) {
      console.error('Failed to send mail', err)
    }
  }
}

function validateForm(): boolean {
  errors.name    = name.value.trim().length >= 3
    ? '' : 'Name must be at least 3 characters.'
  errors.email   = EMAIL_REGEX.test(email.value)
    ? '' : 'Please enter a valid email address.'
  errors.contact = contact.value.trim().length >= 3
    ? '' : 'Please enter a valid contact.'
  errors.message = message.value.trim().length >= 10
    ? '' : 'Message must be at least 10 characters.'

  // return true only if there are no errors
  return !(
    errors.name ||
    errors.email ||
    errors.contact ||
    errors.message
  )
}

/**
 * On first click, flip validationMode on,
 * run a full validation, and only proceed if OK.
 */
function handleSubmit() {
  validationMode.value = true
  if (!validateForm()) return
  processSubmit()
}

//
// — Per-field watchers for live re-validation —
// only active once validationMode is true
//
watch(name, val => {
  if (!validationMode.value) return
  errors.name = val.trim().length >= 3
    ? '' : 'Name must be at least 3 characters.'
})

watch(email, val => {
  if (!validationMode.value) return
  errors.email = EMAIL_REGEX.test(val)
    ? '' : 'Please enter a valid email address.'
})

watch(contact, val => {
  if (!validationMode.value) return
  errors.contact = val.trim().length >= 3
    ? '' : 'Please enter a valid contact.'
})

watch(message, val => {
  if (!validationMode.value) return
  errors.message = val.trim().length >= 10
    ? '' : 'Message must be at least 10 characters.'
})
</script>

<template>
  <Frame class="mpd-form contact-form standard" w="grid g-1 gpy-1.5 max-w-80% mx-auto">
    <transition name="fade" mode="out-in">
      <!-- FORM PANEL -->
      <Frame v-if="formPanel==='form'" key="form" class="form-panel grid form" w="grid g-1">
        <!-- validation notifications wrapper (unused text, can style as you like) -->
        <Frame v-if="notifications" class="sec-notifications">
          <div class="sec-notification error flex items-center g-0.5">
						<i class="i i-mdi:alert-circle flex" />
						<span>
							Please fix the errors below before submitting.
						</span>
          </div>
        </Frame>

        <Frame class="sec-fields" w="grid g-1">
          <Frame>
            <Frame class="field">
              <label>
                <span class="label">Name</span>
                <InputText v-model="name" placeholder="Your fullname" />
              </label>
              <span v-if="errors.name" class="error-message">{{ errors.name }}</span>
            </Frame>
          </Frame>

          <Frame w="flex flex-col g-1 tm:flex-row">
            <Frame class="field flex-1">
              <label>
                <span class="label">Email</span>
                <InputText v-model="email" placeholder="Email address" />
              </label>
              <span v-if="errors.email" class="error-message">{{ errors.email }}</span>
            </Frame>
            <Frame class="field flex-1">
              <label>
                <span class="label">Contact number</span>
                <InputText v-model="contact" placeholder="Contact number" />
              </label>
              <span v-if="errors.contact" class="error-message">{{ errors.contact }}</span>
            </Frame>
          </Frame>

          <Frame>
            <Frame class="field">
              <label>
                <span class="label">Message</span>
                <Textarea v-model="message" placeholder="How can we help you?" />
              </label>
              <span v-if="errors.message" class="error-message">{{ errors.message }}</span>
            </Frame>
          </Frame>
        </Frame>

        <Frame class="sec-actions">
          <Frame>
            <Frame class="field">
              <!-- call handleSubmit instead of processSubmit directly -->
              <Button
                class="mpd btn-submit btn-primary flex items-center"
                :loading="working"
                :disabled="working || !isFormValid"
                @click="handleSubmit"
              >
                <div class="sec-icon">
                  <transition name="fade" mode="out-in">
                    <Spinner v-if="working" key="sending" />
                    <i v-else class="i i-mdi:send" key="form" />
                  </transition>
                </div>
                <div class="sec-label fs--1">
                  <transition name="fade" mode="out-in">
                    <span v-if="working" key="sending">Sending...</span>
                    <span v-else key="form">Submit</span>
                  </transition>
                </div>
              </Button>
            </Frame>
          </Frame>
        </Frame>

        <Frame class="sec-additional">
          <Frame>
            <Frame class="field">
              <i class="i i-mdi:information"></i>
              <span>You will receive a response within 24 hours</span>
            </Frame>
          </Frame>
        </Frame>
      </Frame>

      <!-- SUCCESS PANEL (unchanged) -->
      <Frame v-else key="success" class="form-panel success">
        <Frame class="success-wrapper">
          <div class="icon">
            <div class="lottie-wrap">
              <Lottie :autoplay="true" name="email" />
            </div>
          </div>

          <transition name="fade" mode="out-in" appear>
            <div v-if="!sendDelay" key="sending" class="sending">
              <p>Finalising...</p>
            </div>
            <div v-else key="sent" class="content">
              <h3>Thank you,</h3>
              <p>Your submission has been received by our team, we will be in contact with you soon.</p>
            </div>
          </transition>
        </Frame>
      </Frame>
    </transition>
  </Frame>
</template>

<style lang="scss" scoped>
.contact-form.standard {
	


	border-radius: var(--cardBorderRadius);
	box-shadow: var(--cardShadowLarge);

  @apply shadow-xl;


	.sec-notifications {
		
		
		margin-bottom: gr(1);
		.sec-notification {
			padding: gr(0.5) gr(1);
			border-radius: var(--cardBorderRadius);
			background-color: var(--rose-50);
			color: var(--rose-600);
			font-size: fs(-1);
			.sec-icon {
				margin-right: gr(0.5);
			}
		}
	}
	form-panel.success {
			.success-wrapper {
				display: flex;
				flex-direction: column;
				align-items: center;
				justify-content: center;
				gap: gr(2);

				.icon {
					.lottie-wrap {
						width: 300px;
						height: 300px;
					}
				}

				.content {
					text-align: center;

					h3 {
						font-size: 1.5rem;
						font-weight: 500;
						margin-bottom: gr(1);
					}

					p {
						margin-bottom: gr(1);
					}

					.form-link {
						margin-top: gr(2);
					}
				}
		}

		
	}

	.sec-fields {
		.label {
			color: var(--base-600) !important;
		}
		.field {
			.error-message {
				color: var(--rose-600);
				font-size: fs(-1);
			}

      input, textarea {
        &::placeholder {
          font-size: fs(-0.5);
        }

        border-radius: 0.33rem;
      }

      textarea {
        padding: gr(0.5) gr(0.5);
      }
		}
	}
}
</style>
